// auditpm-v7
import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { format, parseISO } from 'date-fns'
import { FirmHeader, AuditPlanCard } from '../components/PublicComponents'
import { emailHtml } from '../lib/emailHtml'
import { toast, failed } from '../lib/toast'

// Auditor conflict-of-interest declaration — the per-engagement questions.
// EVERY question traces to a risk in the AO's impartiality register, form MR-QR-02 "Management of
// Impartiality" Rev L (the register SAC requires the AO to use). The `ref` on each question is that
// register risk, so an assessor can follow a declaration straight back to the risk it controls.
// A "Yes" on a High question blocks the audit until the office resolves it; a Medium routes to a
// recorded QM decision (M&P 2.4).
// IDs are stable on purpose: q1–q7 keep the meaning they had when earlier declarations were signed, so
// a historical declaration still reads correctly. q8 and q9 are new — the two register risks the old
// question set did not ask about (in-house training within 2 years; gifts / benefits).
const COI_QUESTIONS = [
  {
    id: 'q1',
    risk: 'High',
    category: 'Prior involvement',
    ref: 'MR-QR-02 R1, R5, R6, R9',
    text: 'Are you currently, or have you been within the past 2 years, employed or engaged by this client — or have you provided consultancy, internal audit, or management-system development or implementation services (WSH, SHMS, QMS or EMS) to this client or any of its projects?',
  },
  {
    id: 'q2',
    risk: 'High',
    category: 'Current involvement',
    ref: 'MR-QR-02 R2',
    text: 'Are you currently involved in any project, service, or support work for this client?',
  },
  {
    id: 'q3',
    risk: 'High',
    category: 'Statutory / WSH role',
    ref: 'MR-QR-02 R9',
    text: 'Are you currently, or have you recently been, acting in any statutory or safety role (e.g. WSH Officer) for this client or any of its projects?',
  },
  {
    id: 'q8',
    risk: 'High',
    category: 'In-house training provided',
    ref: 'MR-QR-02 R10',
    text: 'Have you delivered in-house training to this client within the past 2 years? (Public or open-enrolment classes do not need to be declared.)',
  },
  {
    id: 'q9',
    risk: 'High',
    category: 'Gifts, benefits or inducements',
    ref: 'MR-QR-02 R3',
    text: 'Has any financial or non-financial gain, gift, hospitality or other benefit been offered to you, or accepted by you, by or on behalf of this client?',
  },
  {
    id: 'q4',
    risk: 'Medium',
    category: 'Personal / family relationship',
    ref: 'MR-QR-02 R13, R14',
    text: 'Do you have any personal or financial relationship with this client or its personnel — including any close family member in a management, ownership or decision-making role — that may affect your impartiality?',
  },
  {
    id: 'q5',
    risk: 'Medium',
    category: 'External influence / pressure',
    ref: 'MR-QR-02 R7, R11',
    text: 'Are you aware of any commercial, financial, or other pressure — from outside CCIS or from within — that may influence your judgement or the audit outcome?',
  },
  {
    id: 'q6',
    risk: 'Medium',
    category: 'Repeated audit / familiarity',
    ref: 'MR-QR-02 R4',
    text: 'Have you audited this client for 2 or more consecutive years?',
  },
  {
    id: 'q7',
    risk: 'Low',
    category: 'Other conflicts',
    ref: 'MR-QR-02 — catch-all',
    text: 'Are you aware of any other situation that may present a conflict of interest or affect your impartiality?',
  },
]

function calcRiskLevel(answers) {
  const yesQuestions = COI_QUESTIONS.filter(q => answers[q.id] === 'Yes')
  if (yesQuestions.some(q => q.risk === 'High')) return 'High'
  if (yesQuestions.some(q => q.risk === 'Medium')) return 'Medium'
  if (yesQuestions.some(q => q.risk === 'Low')) return 'Low'
  return 'None'
}

function RiskBadge({ level }) {
  const styles = {
    High:   { background: '#FDECEA', color: '#791F1F', border: '0.5px solid #E24B4A' },
    Medium: { background: '#FFF3E0', color: '#854F0B', border: '0.5px solid #EF9F27' },
    Low:    { background: '#E0F2F2', color: '#004a4a', border: '0.5px solid #006363' },
    None:   { background: '#EAF3DE', color: '#27500a', border: '0.5px solid #639922' },
  }
  const labels = { High: '⛔ High risk', Medium: '⚠️ Medium risk', Low: '✓ Low risk', None: '✓ No conflict' }
  return (
    <span style={{ fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 20, ...styles[level] }}>
      {labels[level]}
    </span>
  )
}


function DeclarationCertificate({ job, coi }) {
  if (!coi) return <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>Loading…</div>
  const riskLevel = coi.risk_level || calcRiskLevel(coi.answers || {})
  return (
    <div>
      <div style={{ background: '#fff', borderRadius: 12, padding: 'clamp(16px, 4vw, 32px)', border: '0.5px solid rgba(0,0,0,0.1)' }}>
        <FirmHeader />
        <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 16, textAlign: 'center' }}>Declaration of Impartiality</div>
        <div style={{ background: '#f7f6f3', borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontSize: 13 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '130px 1fr', gap: '4px 8px' }}>
            <span style={{ color: '#888' }}>Auditor</span><strong>{coi.auditor_name || '—'}</strong>
            <span style={{ color: '#888' }}>Client</span>
            {Array.isArray(job.companies) && job.companies.length > 1
              ? <div>{job.companies.map((co, i) => <div key={i}><strong>({i + 1}) {co.name || '—'}</strong>{co.uen ? ` (UEN ${co.uen})` : ''}</div>)}</div>
              : <strong>{job.clients?.name || '—'}</strong>}
            <span style={{ color: '#888' }}>Audit date</span><span>{job.audit_date ? format(parseISO(job.audit_date), 'd MMM yyyy') : '—'}</span>
            <span style={{ color: '#888' }}>Audit type</span><span>{job.audit_type}</span>
            <span style={{ color: '#888' }}>Risk level</span><RiskBadge level={riskLevel} />
          </div>
        </div>
        <div style={{ marginBottom: 16 }}>
          {COI_QUESTIONS.map((q, i) => {
            const ans = (coi.answers || {})[q.id]
            const remark = (coi.remarks || {})[q.id]
            return (
              <div key={q.id} style={{ padding: '8px 0', borderBottom: '0.5px solid rgba(0,0,0,0.06)', fontSize: 13 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <span style={{ color: '#aaa', marginRight: 6, fontSize: 11 }}>{i + 1}.</span>
                    <span style={{ fontSize: 11, color: '#888', marginRight: 6 }}>[{q.risk}]</span>
                    {q.text}
                  </div>
                  <span style={{ fontWeight: 600, color: ans === 'Yes' ? '#a32d2d' : '#27500a', minWidth: 28, flexShrink: 0 }}>{ans || '—'}</span>
                </div>
                {remark && <div style={{ fontSize: 12, color: '#888', marginTop: 4, paddingLeft: 20, fontStyle: 'italic' }}>Note: {remark}</div>}
              </div>
            )
          })}
        </div>
        <div style={{ background: '#f7f6f3', borderRadius: 8, padding: '12px 16px', marginBottom: 14, fontSize: 13, color: '#555', fontStyle: 'italic', borderLeft: '3px solid #006363' }}>
          "I confirm that the above declaration is true and complete to the best of my knowledge."
        </div>
        <div style={{ background: riskLevel === 'High' ? '#FDECEA' : riskLevel === 'Medium' ? '#FFF3E0' : '#eaf3de', borderRadius: 8, padding: '14px 16px', fontSize: 13 }}>
          <div style={{ fontWeight: 600, marginBottom: 10, color: riskLevel === 'High' ? '#791f1f' : riskLevel === 'Medium' ? '#854F0B' : '#27500a' }}>
            <RiskBadge level={riskLevel} /> &nbsp; Declaration recorded
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '130px 1fr', gap: '4px 8px' }}>
            <span style={{ color: '#555' }}>Submitted on</span><strong>{coi.submitted_at ? new Date(coi.submitted_at).toLocaleString('en-SG', { timeZone: 'Asia/Singapore' }) : '—'}</strong>
            <span style={{ color: '#555' }}>IP address</span><strong>{coi.ip_address || '—'}</strong>
            <span style={{ color: '#555' }}>Browser</span><span style={{ fontSize: 11, wordBreak: 'break-all' }}>{coi.user_agent ? coi.user_agent.slice(0, 100) + '…' : '—'}</span>
          </div>
        </div>
        <div style={{ marginTop: 14, textAlign: 'center' }}>
          <button onClick={() => window.print()} style={{ padding: '8px 20px', borderRadius: 8, border: '0.5px solid rgba(0,0,0,0.2)', background: '#fff', cursor: 'pointer', fontSize: 13 }}>
            🖨️ Print / Save as PDF
          </button>
        </div>
      </div>
    </div>
  )
}

export default function AuditorCOIForm() {
  const { token } = useParams()
  const [job, setJob] = useState(null)
  const [auditorSlot, setAuditorSlot] = useState(null)
  const [answers, setAnswers] = useState({})
  const [remarks, setRemarks] = useState({})
  const [submitted, setSubmitted] = useState(false)
  const [submittedCoi, setSubmittedCoi] = useState(null)
  const [invalid, setInvalid] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [loading, setLoading] = useState(true)
  const [alreadyDone, setAlreadyDone] = useState(false)
  const [existingCoi, setExistingCoi] = useState(null)
  const [staleLink, setStaleLink] = useState(false)

  // Warn if auditor tries to close/navigate away without submitting
  useEffect(() => {
    function handleBeforeUnload(e) {
      if (submitted || alreadyDone || job?.status === 'Cancelled') return
      e.preventDefault()
      e.returnValue = 'You have not submitted your Declaration of Impartiality. Are you sure you want to leave?'
      return e.returnValue
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [submitted, alreadyDone, job])

  useEffect(() => { loadForm() }, [token])

  async function loadForm() {
    // Token-scoped read via RPC (returns the job + its COI responses)
    const { data: found, error } = await supabase.rpc('get_job_by_coi_token', { p_token: token })
    if (error || !found || !found.id) { setInvalid(true); setLoading(false); return }
    setJob(found)
    const tokens = found.auditor_coi_tokens || {}
    const slotEntry = Object.entries(tokens).find(([k, v]) => v === token)
    const slot = slotEntry ? slotEntry[0] : null
    if (slot) setAuditorSlot(slot)
    // Current-state only: a prior declaration counts solely if it belongs to the auditor CURRENTLY
    // assigned to this slot. After an auditor change the slot token is unchanged, so matching by token
    // alone would surface the previous auditor's certificate. Match by the current auditor name instead;
    // if none exists for them, fall through to the submission form. (Token is not regenerated.)
    const currentAuditorName = slot === 'auditor2' ? found.auditor2_name : found.auditor_name
    // Strict: each token is tied to the auditor it was ISSUED for (auditor_coi_token_names). If the
    // assigned auditor has since changed — or the slot is now unassigned — the old link is no longer
    // valid: never show a previous auditor's form or data. (Legacy jobs without the mapping fall back
    // to the slot + current-name check below.)
    const tokenAuditor = (found.auditor_coi_token_names || {})[slot]
    if (!slot || !currentAuditorName || (tokenAuditor && tokenAuditor !== currentAuditorName)) {
      setStaleLink(true); setLoading(false); return
    }
    const existing = (found.coi_responses || []).find(c =>
      c.auditor_slot === slot && c.auditor_name === currentAuditorName) || null
    if (existing) { setAlreadyDone(true); setExistingCoi(existing) }
    setLoading(false)
  }

  function setAll(val) {
    const a = {}
    COI_QUESTIONS.forEach(q => { a[q.id] = val })
    setAnswers(a)
  }

  function hasAnyYes() { return Object.values(answers).some(v => v === 'Yes') }
  function allAnswered() { return COI_QUESTIONS.every(q => answers[q.id] === 'Yes' || answers[q.id] === 'No') }

  async function submit() {
    if (!allAnswered()) { toast('Answer every question', 'warning'); return }
    setSubmitting(true)
    const riskLevel = calcRiskLevel(answers)
    const flagged = riskLevel === 'High' || riskLevel === 'Medium'
    // Derive slot directly from job tokens — don't rely on async auditorSlot state
    const tokens = job.auditor_coi_tokens || {}
    const slotEntry = Object.entries(tokens).find(([k, v]) => v === token)
    const resolvedSlot = slotEntry ? slotEntry[0] : (auditorSlot || 'auditor1')
    const auditorName = resolvedSlot === 'auditor2' ? job.auditor2_name : job.auditor_name
    let ipAddress = 'unknown'
    try {
      const ipRes = await fetch('https://api.ipify.org?format=json')
      ipAddress = (await ipRes.json()).ip
    } catch (e) {}
    const submittedAt = new Date().toISOString()
    // Response insert + admin alert (if flagged) happen server-side
    const { error } = await supabase.rpc('submit_auditor_coi', {
      p_token: token, p_answers: answers, p_remarks: remarks,
      p_risk_level: riskLevel, p_flagged: flagged,
      p_ip: ipAddress, p_ua: navigator.userAgent,
    })
    if (error) {
      failed("Couldn't submit your declaration", error.message, 'Try again')
      setSubmitting(false)
      return
    }
    // Best-effort: email the office when a conflict is flagged, so it isn't only surfaced in-app.
    // The server-side admin_alert still fires regardless; this notification is non-blocking.
    if (flagged) {
      try {
        const jobUrl = `${window.location.origin}/jobs/${job.id}`
        await supabase.functions.invoke('send-internal-email', {
          body: {
            to: 'bizSAFE@ccis.com.sg',
            subject: `⚠ COI flagged — ${auditorName} (${job.clients?.name || 'audit'})`,
            html: emailHtml({
              title: 'Conflict of interest flagged',
              greeting: 'Dear Team,',
              lines: ['An auditor has declared a conflict of interest. Please review it and record a decision in AuditPM.'],
              details: [['Auditor', auditorName], ['Client', job.clients?.name || '—'], ['Risk level', String(riskLevel || '—')]],
              button: { label: 'Review & decide →', url: jobUrl },
            }),
          },
        })
      } catch (e) { /* non-blocking — the in-app alert + dashboard still surface it */ }
    }
    setSubmittedCoi({ auditor_name: auditorName, answers, remarks, flagged, risk_level: riskLevel, ip_address: ipAddress, user_agent: navigator.userAgent, submitted_at: submittedAt })
    setSubmitted(true)
    setSubmitting(false)
  }

  if (loading) return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5fafa' }}><div style={{ color: '#999' }}>Loading…</div></div>
  if (invalid) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5fafa', padding: 20 }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 32, maxWidth: 440, textAlign: 'center', border: '0.5px solid rgba(0,0,0,0.12)' }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>🔗</div>
        <h2 style={{ fontWeight: 500, marginBottom: 8 }}>Invalid link</h2>
        <p style={{ color: '#666', fontSize: 14 }}>This declaration link is not valid. Please contact your audit coordinator.</p>
      </div>
    </div>
  )

  // Cancelled audit — no declaration needed (a database rule also refuses a submission).
  if (job?.status === 'Cancelled') return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5fafa', padding: 20 }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 32, maxWidth: 440, textAlign: 'center', border: '0.5px solid rgba(0,0,0,0.12)' }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>⚠️</div>
        <h2 style={{ fontWeight: 500, marginBottom: 8 }}>This audit has been cancelled</h2>
        <p style={{ color: '#666', fontSize: 14 }}>No declaration is needed. Please contact your audit coordinator if you have any questions.</p>
      </div>
    </div>
  )

  if (staleLink) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5fafa', padding: 20 }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 32, maxWidth: 440, textAlign: 'center', border: '0.5px solid rgba(0,0,0,0.12)' }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>🔗</div>
        <h2 style={{ fontWeight: 500, marginBottom: 8 }}>Link no longer valid</h2>
        <p style={{ color: '#666', fontSize: 14 }}>This link is no longer valid for the assigned auditor. Please contact your audit coordinator for an updated link.</p>
      </div>
    </div>
  )

  if (alreadyDone && existingCoi) return (
    <div style={{ minHeight: '100vh', background: '#f5fafa', padding: '40px 20px 60px' }}>
      <div style={{ maxWidth: 640, margin: '0 auto' }}>
        <FirmHeader />
        <p style={{ fontSize: 13, color: '#888', marginBottom: 16, textAlign: 'center' }}>You have already submitted your declaration. Here is your certificate for reference.</p>
        <AuditPlanCard job={job} />
        <DeclarationCertificate job={job} coi={existingCoi} />
      </div>
    </div>
  )

  if (submitted) return (
    <div style={{ minHeight: '100vh', background: '#f5fafa', padding: '40px 20px 60px' }}>
      <div style={{ maxWidth: 640, margin: '0 auto' }}>
        <FirmHeader />
        <p style={{ fontSize: 13, color: '#27500a', marginBottom: 16, textAlign: 'center', fontWeight: 500 }}>✓ Declaration submitted successfully</p>
        <AuditPlanCard job={job} />
        <DeclarationCertificate job={job} coi={submittedCoi} />
      </div>
    </div>
  )

  const auditorName = auditorSlot === 'auditor2' ? job.auditor2_name : job.auditor_name
  const currentRisk = calcRiskLevel(answers)

  return (
    <div style={{ minHeight: '100vh', background: '#f5fafa', padding: '40px 20px 60px' }}>
      {/* Sticky banner — always visible until submitted */}
      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 999,
        background: '#c0392b', color: '#fff',
        padding: '12px 20px', display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 13, fontWeight: 500, boxShadow: '0 -2px 12px rgba(0,0,0,0.2)',
      }}>
        ⚠ You have not submitted your declaration yet — please scroll down and submit before closing this page.
      </div>
      <div style={{ maxWidth: 640, margin: '0 auto', paddingBottom: 56 }}>
        <FirmHeader />
        <div style={{ marginBottom: 20 }}>
          <h1 style={{ fontWeight: 500, fontSize: 20, marginBottom: 6 }}>Declaration of Impartiality</h1>
          <p style={{ color: '#666', fontSize: 14 }}>Dear <strong>{auditorName}</strong>, please review the audit details and answer all questions honestly before the audit date.</p>
        </div>

        <AuditPlanCard job={job} />

        <div style={{ background: '#fff', borderRadius: 12, padding: '20px 24px', border: '0.5px solid rgba(0,0,0,0.12)', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
            <h2 style={{ fontWeight: 500, fontSize: 15 }}>Conflict of interest declaration</h2>
            {Object.keys(answers).length > 0 && <RiskBadge level={currentRisk} />}
          </div>
          <p style={{ fontSize: 13, color: '#666', marginBottom: 16 }}>Please answer all questions honestly. This declaration is required under SAC CT 17 and ISO 17021.</p>
          <div style={{ display: 'flex', gap: 8, marginBottom: 20, paddingBottom: 16, borderBottom: '0.5px solid rgba(0,0,0,0.1)' }}>
            <button onClick={() => setAll('No')} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #006363', background: '#fff', color: '#006363', fontWeight: 500, cursor: 'pointer', fontSize: 13, transition: 'background 0.15s, color 0.15s' }}
              onMouseEnter={e => { e.currentTarget.style.background='#006363'; e.currentTarget.style.color='#fff' }}
              onMouseLeave={e => { e.currentTarget.style.background='#fff'; e.currentTarget.style.color='#006363' }}>✓ No to all</button>
            <button onClick={() => { setAnswers({}); setRemarks({}) }} style={{ padding: '8px 12px', borderRadius: 8, border: '0.5px solid rgba(0,0,0,0.2)', background: '#fff', color: '#666', cursor: 'pointer', fontSize: 13 }}>Clear</button>
          </div>
          {COI_QUESTIONS.map((q, i) => (
            <div key={q.id} style={{ padding: '12px 0', borderBottom: i < COI_QUESTIONS.length - 1 ? '0.5px solid rgba(0,0,0,0.07)' : 'none' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 10 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, color: '#888', marginBottom: 3, display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span>{i + 1}. {q.category}</span>
                    <span style={{ padding: '1px 7px', borderRadius: 10, fontSize: 10, fontWeight: 600,
                      background: q.risk === 'High' ? '#FDECEA' : q.risk === 'Medium' ? '#FFF3E0' : '#E0F2F2',
                      color: q.risk === 'High' ? '#791F1F' : q.risk === 'Medium' ? '#854F0B' : '#004a4a' }}>
                      {q.risk}
                    </span>
                  </div>
                  <div style={{ fontSize: 14, lineHeight: 1.5 }}>{q.text}</div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                {['No', 'Yes'].map(opt => (
                  <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 8, cursor: 'pointer',
                    border: `0.5px solid ${answers[q.id] === opt ? (opt === 'Yes' ? '#a32d2d' : '#27500a') : 'rgba(0,0,0,0.2)'}`,
                    background: answers[q.id] === opt ? (opt === 'Yes' ? '#fcebeb' : '#eaf3de') : '#fff',
                    fontWeight: answers[q.id] === opt ? 500 : 400, fontSize: 13,
                    color: answers[q.id] === opt ? (opt === 'Yes' ? '#a32d2d' : '#27500a') : '#333' }}>
                    <input type="radio" name={q.id} value={opt} checked={answers[q.id] === opt}
                      onChange={() => setAnswers(a => ({ ...a, [q.id]: opt }))} style={{ display: 'none' }} />
                    {opt}
                  </label>
                ))}
              </div>
              {answers[q.id] === 'Yes' && (
                <textarea placeholder="Please provide details…" value={remarks[q.id] || ''}
                  onChange={e => setRemarks(r => ({ ...r, [q.id]: e.target.value }))}
                  style={{ width: '100%', marginTop: 8, padding: '8px 10px', border: '0.5px solid #a32d2d', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', background: '#fffafa', resize: 'vertical', minHeight: 60, outline: 'none' }} />
              )}
            </div>
          ))}
        </div>

        {hasAnyYes() && (
          <div style={{ background: currentRisk === 'High' ? '#FDECEA' : '#FFF3E0', border: `0.5px solid ${currentRisk === 'High' ? '#E24B4A' : '#EF9F27'}`, borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontSize: 13, color: currentRisk === 'High' ? '#791f1f' : '#854F0B' }}>
            {currentRisk === 'High'
              ? '⛔ High risk — you have declared a critical conflict of interest. Submitting will notify your audit coordinator. The audit plan cannot proceed until this is reviewed.'
              : '⚠️ Medium risk — you have declared a potential conflict. Management approval will be required before the audit proceeds.'}
          </div>
        )}

        <div style={{ background: '#fff', borderRadius: 12, padding: '16px 20px', border: '0.5px solid rgba(0,0,0,0.12)', marginBottom: 20, fontSize: 13, color: '#555', borderLeft: '3px solid #006363' }}>
          "I confirm that the above declaration is true and complete to the best of my knowledge."
        </div>

        <button onClick={submit} disabled={!allAnswered() || submitting}
          style={{ width: '100%', padding: '12px 20px', borderRadius: 10, border: 'none', background: allAnswered() ? '#006363' : '#ccc', color: '#fff', fontWeight: 500, fontSize: 15, cursor: allAnswered() ? 'pointer' : 'not-allowed', fontFamily: 'inherit' }}>
          {submitting ? 'Submitting…' : 'Submit declaration'}
        </button>
        <p style={{ textAlign: 'center', color: '#aaa', fontSize: 12, marginTop: 16 }}>CCIS Singapore Pte Ltd · query@ccis.com.sg</p>
      </div>
    </div>
  )
}